import * as THREE from 'three';

/**
 * Handles user interactions, clicks, and teleportation logic
 */
export class InteractionHandler {
  constructor(scene, camera, teleportationSystem, cameraController) {
    this.scene = scene;
    this.camera = camera;
    this.teleportationSystem = teleportationSystem;
    this.cameraController = cameraController;
    this.raycaster = new THREE.Raycaster();
    
    // Callbacks
    this.onInteractionCallback = null;
    this.onRoomChangeCallback = null;
  }

  /**
   * Handle interaction (click/tap) events
   */
  handleInteraction(event, floorMeshes = [], floor = null, interactiveObjects = [], visibleRoomObjects = null) {
    const pointer = new THREE.Vector2(
      (event.clientX / window.innerWidth) * 2 - 1,
      -(event.clientY / window.innerHeight) * 2 + 1
    );

    this.raycaster.setFromCamera(pointer, this.camera);

    // Include all floor meshes for custom rooms, or just the main floor for procedural rooms
    const availableFloorMeshes = floorMeshes && floorMeshes.length > 0 ? floorMeshes : [floor];
    const intersectables = [...availableFloorMeshes.filter(f => f), ...interactiveObjects];
    const intersects = this.raycaster.intersectObjects(intersectables, true);

    if (intersects.length === 0) return;

    const firstHit = intersects[0].object;
    let targetObject = firstHit;

    // Find parent with userData
    while (targetObject.parent && !targetObject.userData.item) {
      targetObject = targetObject.parent;
    }

    if (targetObject.userData && targetObject.userData.item) {
      // Clicked artwork
      this.handleArtworkClick(targetObject.userData.item, targetObject.userData.placement, visibleRoomObjects);
    } else if (firstHit.name === "floor") {
      // Clicked floor - teleport to that location with safety checks
      this.handleFloorClick(intersects[0], firstHit, visibleRoomObjects);
    } else {
      console.log('Clicked object:', firstHit.name, 'type:', firstHit.type);
    }
  }

  /**
   * Handle artwork click/tap
   */
  handleArtworkClick(item, placement, visibleRoomObjects = null) {
    // Check if it's a 3D model by file extension  
    const is3D = item.url && (item.url.endsWith('.glb') || item.url.endsWith('.gltf'));
    
    if (is3D) {
      // Check if we need to change rooms first
      const objectPosition = new THREE.Vector3(placement.positionX, placement.positionY, placement.positionZ);
      if (!this.teleportationSystem.isInSameRoom(this.camera.position, objectPosition)) {
        console.log('Object is in a different room, need to load room first');
        
        // Trigger room change event if callback is available
        if (this.onInteractionCallback) {
          this.onInteractionCallback('cross-room-teleport', { 
            item, 
            placement,
            targetShowroomId: placement.showroomId,
            message: 'Object is in a different room. Please switch to that room first.'
          });
        } else {
          console.warn('Cannot teleport to different room - no room change callback available');
        }
        return;
      }
    }

    // Use the unified teleportation method
    this.handleArtworkTeleportation(item, placement, false, true, visibleRoomObjects);
  }

  /**
   * Handle floor click/tap for teleportation
   */
  handleFloorClick(intersect, firstHit, visibleRoomObjects = null) {
    const point = intersect.point;
    let targetPosition = point.clone();
    targetPosition.y = 1.8;
    
    // Check if we can travel to the clicked position without passing through walls
    // Pass visible room objects to limit collision check to current room only
    if (!this.teleportationSystem.canTravelTo(this.camera.position, targetPosition, visibleRoomObjects)) {
      console.warn('Cannot teleport through walls to clicked position');
      return;
    }
    
    // Check if the clicked position is safe (not too close to walls)
    if (!this.teleportationSystem.isPositionSafe(targetPosition, 1.0)) {
      // Find the nearest safe position
      const safePosition = this.teleportationSystem.findNearestSafePosition(targetPosition);
      if (safePosition && this.teleportationSystem.canTravelTo(this.camera.position, safePosition, visibleRoomObjects)) {
        targetPosition = safePosition;
        console.log('Adjusted teleportation to safe position:', targetPosition);
      } else {
        console.warn('No safe teleportation position found near clicked point');
        return; // Don't teleport if no safe position is available
      }
    }
    
    const floorId = firstHit.userData?.floorId || 'main';
//    console.log('Teleporting to floor position:', targetPosition, 'from floor mesh:', firstHit.name, 'floor ID:', floorId);
    this.cameraController.animateCamera(targetPosition, null);
  }

  /**
   * Handle teleportation to an object in a different room
   */
  async teleportToObjectInRoom(item, placement, showroomData, allPlacements, allItems, roomManager, artworkManager) {
    try {
      // Load the target room
      await roomManager.createRoom(showroomData);
      
      // Add all items for that room
      const roomPlacements = allPlacements.filter(p => p.showroomId === showroomData.id);
      roomPlacements.forEach(p => {
        const roomItem = allItems.find(i => i.id === p.itemId);
        if (roomItem) {
          artworkManager.addArtwork(roomItem, p, roomManager.getPlacementSystem());
        }
      });
      
      // Wait a bit for scene to stabilize
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Now teleport to the object
      this.handleArtworkTeleportation(item, placement, true, false, null);
      
    } catch (error) {
      console.error('Failed to teleport to object in different room:', error);
    }
  }

  /**
   * Extract artwork teleportation logic for reuse
   */
  handleArtworkTeleportation(item, placement, fromCatalog = false, showPopup = true, visibleRoomObjects = null) {
    let targetPosition, lookAtPosition;
    
    // Check if it's a 3D model by file extension
    const is3D = item.url && (item.url.endsWith('.glb') || item.url.endsWith('.gltf'));
    
    if (is3D) {
      // Use the same wall-normal approach for 3D objects
      const objectPosition = new THREE.Vector3(placement.positionX, placement.positionY, placement.positionZ);
      
      // Primary approach: wall-normal positioning
      let bestPosition = this.teleportationSystem.findPositionPerpendicularToClosestWall(objectPosition, 2.5);
      
      // Validate position based on whether this is from catalog or not
      const validatePosition = (pos) => {
        if (!pos) return false;
        
        const hasLineOfSight = this.teleportationSystem.hasLineOfSight(pos, objectPosition);
        const isPositionSafe = this.teleportationSystem.isPositionSafe(pos, 1.0);
        
        if (fromCatalog) {
          // For catalog teleportation, don't check travel path
          return isPositionSafe && hasLineOfSight;
        } else {
          // For normal teleportation, if you can see and click the object, you should be able to teleport
          return isPositionSafe && hasLineOfSight;
        }
      };
      
      if (validatePosition(bestPosition)) {
        console.log('Using wall-normal positioning for 3D object teleportation (catalog)');
      } else if (bestPosition) {
        // Try closer distance
        bestPosition = this.teleportationSystem.findPositionPerpendicularToClosestWall(objectPosition, 2.0);
        if (validatePosition(bestPosition)) {
          console.log('Using closer wall-normal positioning for 3D object teleportation (catalog)');
        } else {
          bestPosition = null;
        }
      }
      
      // Fallback: circular positioning
      if (!bestPosition) {
        console.log('Wall-normal approach failed, trying circular fallback (catalog)');
        const viewingAngles = [placement.rotationY + Math.PI, placement.rotationY + Math.PI * 0.75, placement.rotationY + Math.PI * 1.25];
        
        for (let distance = 2.0; distance >= 1.5; distance -= 0.25) {
          for (const angle of viewingAngles) {
            const testPosition = new THREE.Vector3(
              placement.positionX + Math.sin(angle) * distance,
              1.8,
              placement.positionZ + Math.cos(angle) * distance
            );
            
            if (validatePosition(testPosition)) {
              bestPosition = testPosition;
              break;
            }
          }
          if (bestPosition) break;
        }
      }
      
      if (!bestPosition) {
        console.warn('No safe viewing position found for 3D object');
        return;
      }
      
      targetPosition = bestPosition;
      // Determine if it's a floor placement 
      const isFloorPlacement = placement.positionY <= 0.1;
      
      lookAtPosition = new THREE.Vector3(
        placement.positionX,
        placement.positionY + (isFloorPlacement ? 2.0 : 0), // Floor objects: look at pedestal height, Wall objects: look at placement position
        placement.positionZ
      );
    } else {
      // Painting teleportation - also check if from catalog
      const distance = 1.5;
      const angle = placement.rotationY || 0;

      targetPosition = new THREE.Vector3(
        placement.positionX + Math.sin(angle) * distance,
        1.8,
        placement.positionZ + Math.cos(angle) * distance
      );
      
      // If you can see and click the painting, you should be able to teleport to it
      // No need to check wall collision - remove this restriction
      
      if (!this.teleportationSystem.isPositionSafe(targetPosition, 1.0)) {
        console.warn('Painting position is not safe');
        return;
      }
      
      // Determine if it's a floor placement
      const isFloorPlacement = placement.positionY <= 0.1;
      
      lookAtPosition = new THREE.Vector3(
        placement.positionX,
        placement.positionY + (isFloorPlacement ? 1.5 : 0), // Floor paintings: look higher for pedestal, Wall paintings: look at placement position
        placement.positionZ
      );
    }
    
    this.cameraController.animateCamera(targetPosition, lookAtPosition, () => {
      if (showPopup && this.onInteractionCallback) {
        this.onInteractionCallback('artwork', { item, placement });
      }
    });
  }

  /**
   * Set interaction callback
   */
  setOnInteraction(callback) {
    this.onInteractionCallback = callback;
  }

  /**
   * Set room change callback
   */
  setOnRoomChangeRequest(callback) {
    this.onRoomChangeCallback = callback;
  }
}